import { type ReactNode, isValidElement, forwardRef, cloneElement } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

export interface DropdownItem {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  danger?: boolean;
}

export interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  align?: "start" | "end";
}

/**
 * Proxy component that forwards all Headless UI MenuButton props (onClick, ref,
 * aria-haspopup, aria-expanded, etc.) onto the user-supplied trigger element.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TriggerProxy = forwardRef<HTMLButtonElement, any>(
  function TriggerProxy({ trigger, children: _children, ...props }, ref) {
    if (isValidElement(trigger)) {
      // Spread Headless UI props (onClick, aria-*, etc.) onto the trigger element.
      // `children` is deliberately excluded so the trigger keeps its own content.
      return cloneElement(trigger as React.ReactElement<Record<string, unknown>>, { ...props, ref });
    }
    return <button {...props} ref={ref}>{trigger}</button>;
  }
);

export function Dropdown({ trigger, items, align = "end" }: DropdownProps) {
  return (
    <Menu as="div" className="relative inline-block text-start">
      <MenuButton as={TriggerProxy} trigger={trigger} />

      <MenuItems
        transition
        className={`
          absolute z-10 mt-2 w-56
          ${align === "end" ? "end-0 origin-top-right" : "start-0 origin-top-left"}
          rounded-md bg-white dark:bg-neutral-800
          shadow-lg ring-1 ring-black/5 dark:ring-white/10
          focus:outline-none
          transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0
        `}
      >
        <div className="py-1">
          {items.map((item, index) => (
            <MenuItem key={index} disabled={item.disabled}>
              {({ active, disabled }) => (
                <button
                  onClick={item.onClick}
                  disabled={disabled}
                  className={`
                    flex w-full items-center gap-2 px-4 py-2 text-sm
                    ${
                      item.danger
                        ? "text-danger-600 dark:text-danger-500"
                        : "text-neutral-700 dark:text-neutral-300"
                    }
                    ${active && !disabled ? "bg-neutral-100 dark:bg-neutral-700" : ""}
                    ${disabled ? "opacity-50 cursor-not-allowed" : ""}
                  `}
                >
                  {item.icon && <span className="w-5 h-5 flex-shrink-0">{item.icon}</span>}
                  {item.label}
                </button>
              )}
            </MenuItem>
          ))}
        </div>
      </MenuItems>
    </Menu>
  );
}

export const DropdownButton = forwardRef<
  HTMLButtonElement,
  { children: ReactNode; className?: string }
>(({ children, className = "", ...rest }, ref) => {
  return (
    <button
      ref={ref}
      {...rest}
      className={`
        inline-flex items-center justify-center gap-1
        px-4 py-2 text-sm font-medium
        rounded-md border border-neutral-300 dark:border-neutral-600
        bg-white dark:bg-neutral-800
        text-neutral-700 dark:text-neutral-300
        hover:bg-neutral-50 dark:hover:bg-neutral-700
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500
        ${className}
      `}
    >
      {children}
      <ChevronDownIcon className="w-4 h-4" />
    </button>
  );
});

DropdownButton.displayName = "DropdownButton";
